<template>
  <div
    class="section-picker-overlay"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="`${uid}-title`"
    @click.self="onClose"
    @keydown.esc="onClose"
  >
    <div class="section-picker" :class="{ 'is-narrow': isNarrowViewport }">

      <!-- ───── Header ───── -->
      <header class="popup-header">
        <h2 :id="`${uid}-title`" class="popup-title">
          <template v-if="mode === 'list'">Sections</template>
          <template v-else-if="mode === 'create'">
            <button
              v-if="!createOnly"
              type="button"
              class="back-link"
              @click="mode = 'list'"
            >← Back to sections</button>
            <span v-else>New section</span>
          </template>
          <template v-else-if="mode === 'edit'">
            <button type="button" class="back-link" @click="cancelEdit">← Back to sections</button>
          </template>
        </h2>
        <button
          ref="closeBtn"
          type="button"
          class="close-btn"
          title="Close"
          aria-label="Close"
          @click="onClose"
        >×</button>
      </header>

      <!-- ───── Body: list mode ───── -->
      <div v-if="mode === 'list'" class="popup-body">
        <p v-if="loadError" class="error-banner" role="alert">{{ loadError }}</p>

        <p
          v-if="archivedCurrentBanner"
          class="archived-current-banner"
          role="status"
        >
          The current section "<strong>{{ archivedCurrentBanner }}</strong>" is archived.
          Pick a replacement or turn on <strong>Show archived</strong> to restore it.
        </p>

        <div class="toolbar">
          <button
            type="button"
            class="btn btn-primary new-btn"
            @click="openCreate"
          >+ New section</button>
          <label class="show-archived">
            <input v-model="showArchived" type="checkbox">
            <span>Show archived</span>
          </label>
        </div>

        <p v-if="loading" class="loading-state">Loading sections…</p>

        <div
          v-else-if="visibleSections.length === 0 && !showArchived"
          class="empty-state"
        >
          <p class="empty-text">
            No sections yet. Create the first one to organize your questions
            and questionnaires.
          </p>
          <button
            type="button"
            class="btn btn-primary"
            @click="openCreate"
          >+ New section</button>
        </div>

        <ul v-else class="section-list">
          <li
            v-for="s in visibleSections"
            :key="s.id"
            :class="[
              'section-row',
              {
                'is-current': s.id === currentSectionId,
                'is-archived': s.status === 'archived',
              },
            ]"
          >
            <button
              type="button"
              class="row-main"
              @click="pickSection(s)"
            >
              <div class="row-name">
                {{ displayName(s) }}
                <span
                  v-if="s.id === currentSectionId && s.status === 'archived'"
                  class="current-archived-tag"
                >Current — archived</span>
              </div>
              <div class="row-meta">
                {{ s.question_count }} {{ s.question_count === 1 ? 'question' : 'questions' }}
                ·
                {{ s.questionnaire_count }} {{ s.questionnaire_count === 1 ? 'questionnaire' : 'questionnaires' }}
              </div>
            </button>

            <div class="row-actions" @click.stop>
              <button
                type="button"
                class="icon-btn"
                title="Rename"
                aria-label="Rename"
                @click="openEdit(s)"
              >✎</button>

              <button
                v-if="s.status === 'active'"
                type="button"
                class="icon-btn icon-archive"
                title="Archive"
                aria-label="Archive"
                @click="archiveConfirm = s"
              >×</button>
              <button
                v-else
                type="button"
                class="icon-btn icon-restore"
                title="Restore"
                aria-label="Restore"
                @click="restoreSection(s)"
              >↺</button>

              <button
                type="button"
                class="icon-btn icon-delete"
                :disabled="hasReferences(s)"
                :title="hasReferences(s)
                  ? `Can't delete — ${s.question_count} question(s), ${s.questionnaire_count} questionnaire(s) reference this section`
                  : 'Delete permanently'"
                @click="deleteConfirm = { section: s, typed: '' }"
              >🗑</button>
            </div>
          </li>
        </ul>
      </div>

      <!-- ───── Body: create / edit form ───── -->
      <div v-else class="popup-body popup-body-form">
        <h3 class="form-title">
          <template v-if="mode === 'create'">New section</template>
          <template v-else>Rename section</template>
        </h3>

        <p v-if="saveError" class="error-banner" role="alert">{{ saveError }}</p>

        <div class="field">
          <label class="field-label" :for="`${uid}-slug`">Slug</label>
          <input
            :id="`${uid}-slug`"
            v-model="form.slug"
            type="text"
            class="field-input mono"
            :class="{ 'has-error': !!slugError }"
            :disabled="slugReadOnly"
            autocomplete="off"
            spellcheck="false"
            @input="onSlugInput"
          >
          <p v-if="slugReadOnly" class="field-hint dim">
            The slug can't change because
            <strong>{{ editingRefs.question_count }}</strong>
            {{ editingRefs.question_count === 1 ? 'question' : 'questions' }}
            and
            <strong>{{ editingRefs.questionnaire_count }}</strong>
            {{ editingRefs.questionnaire_count === 1 ? 'questionnaire' : 'questionnaires' }}
            reference it. Renaming the section's display name in any language is unaffected.
          </p>
          <p v-else class="field-hint">
            Lowercase letters, digits, hyphens, or underscores.
            Must start with a letter. 2–64 characters.
          </p>
          <p v-if="slugError" class="field-error">{{ slugError }}</p>
        </div>

        <div class="field">
          <label class="field-label">Name</label>
          <LocaleSelector
            v-model="activeLang"
            :locales="SUPPORTED_LOCALES"
            :labels="LOCALE_LABELS"
            :filled-fn="isLocaleFilled"
            hint="At least one language is required"
          />
          <input
            v-model="form.name[activeLang]"
            type="text"
            class="field-input"
            :class="{ 'has-error': !!nameError }"
            :placeholder="`Name (${LOCALE_LABELS[activeLang]})`"
            autocomplete="off"
          >
          <p v-if="nameError" class="field-error">{{ nameError }}</p>
        </div>

        <div class="form-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="saving"
            @click="mode === 'create' ? (createOnly ? onClose() : (mode = 'list')) : cancelEdit()"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isFormValid || saving"
            @click="mode === 'create' ? submitCreate() : submitEdit()"
          >{{ saving ? 'Saving…' : (mode === 'create' ? 'Create section' : 'Save changes') }}</button>
        </div>
      </div>
    </div>

    <!-- ───── Archive confirm ───── -->
    <div
      v-if="archiveConfirm"
      class="confirm-overlay"
      role="dialog"
      aria-modal="true"
      @click.self="archiveConfirm = null"
    >
      <div class="confirm-modal">
        <h3 class="confirm-title">Archive section "{{ displayName(archiveConfirm) }}"?</h3>
        <p class="confirm-text">
          It will be hidden from the active list but existing references
          continue to work. You can bring it back by turning on
          <strong>Show archived</strong> at the top of this popup and
          clicking <strong>Restore</strong>.
        </p>
        <div class="confirm-footer">
          <button type="button" class="btn btn-secondary" @click="archiveConfirm = null">Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="busy"
            @click="confirmArchive"
          >{{ busy ? 'Archiving…' : 'Archive' }}</button>
        </div>
      </div>
    </div>

    <!-- ───── Delete confirm (typed-slug) ───── -->
    <div
      v-if="deleteConfirm"
      class="confirm-overlay"
      role="dialog"
      aria-modal="true"
      @click.self="deleteConfirm = null"
    >
      <div class="confirm-modal">
        <h3 class="confirm-title">
          Delete section "{{ displayName(deleteConfirm.section) }}" permanently?
        </h3>
        <p class="confirm-text">
          This cannot be undone. The section has no questions or
          questionnaires referencing it. Type the slug
          <code class="mono-inline">{{ deleteConfirm.section.slug }}</code>
          to confirm.
        </p>
        <input
          v-model="deleteConfirm.typed"
          type="text"
          class="field-input mono"
          autocomplete="off"
          spellcheck="false"
          :placeholder="deleteConfirm.section.slug"
        >
        <div class="confirm-footer">
          <button type="button" class="btn btn-secondary" :disabled="busy" @click="deleteConfirm = null">Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="busy || deleteConfirm.typed !== deleteConfirm.section.slug"
            @click="confirmDelete"
          >{{ busy ? 'Deleting…' : 'Delete' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import apiService from '@/services/apiService';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '@/components/admin/_data/questions.js';
import { SLUG_REGEX } from '@/components/admin/_data/cards.js';

let uidCounter = 0;

function blankI18nName() {
  return SUPPORTED_LOCALES.reduce((acc, loc) => { acc[loc] = ''; return acc; }, {});
}

function trimI18n(obj) {
  const out = {};
  for (const loc of SUPPORTED_LOCALES) {
    const v = obj && obj[loc];
    if (typeof v === 'string' && v.trim()) out[loc] = v.trim();
  }
  return out;
}

function hasAnyLocaleFilled(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return SUPPORTED_LOCALES.some(loc => typeof obj[loc] === 'string' && obj[loc].trim());
}

export default {
  name: 'SectionPickerPopup',
  components: { LocaleSelector },
  props: {
    // Highlighted as the user's current selection; also drives the
    // archived-current-section banner when the row isn't in the active
    // list.
    currentSectionId: { type: [Number, null], default: null },
    // Mode to open in. 'create' opens straight on the new-section form and
    // skips the reuse list entirely (back/cancel just close).
    initialMode: { type: String, default: 'list' },
    // The questionnaire to create the section under (sections are
    // per-questionnaire). Required for create mode.
    questionnaireSlug: { type: String, default: '' },
  },
  emits: ['close', 'select-section'],
  data() {
    uidCounter += 1;
    return {
      uid: `secpop-${uidCounter}`,
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      mode: this.initialMode,  // 'list' | 'create' | 'edit'
      sections: [],
      loading: false,
      loadError: null,
      showArchived: false,
      isNarrowViewport: false,
      // Form state (shared between create + edit modes)
      activeLang: DEFAULT_LOCALE,
      form: { slug: '', name: blankI18nName() },
      editingId: null,           // set in edit mode
      editingRefs: { question_count: 0, questionnaire_count: 0 },
      saving: false,
      saveError: null,
      // Confirm dialogs
      archiveConfirm: null,      // section object | null
      deleteConfirm: null,       // { section, typed: '' } | null
      busy: false,               // archive/restore/delete in flight
      _onResize: null,
      _onKeydown: null,
    };
  },
  computed: {
    // When opened in create mode, the reuse list is skipped entirely, so
    // back/cancel close the popup rather than returning to a list.
    createOnly() {
      return this.initialMode === 'create';
    },
    visibleSections() {
      // Filter by status per toggle, then sort alphabetically by
      // displayName in the *active locale* — UI sort, not the DB's
      // English-keyed sort. The backend already returns rows sorted
      // by name.en; we re-sort here so a locale switch reorders the
      // list in-place without a refetch.
      const wanted = this.showArchived
        ? this.sections
        : this.sections.filter(s => s.status === 'active');
      return wanted.slice().sort((a, b) => {
        const an = this.displayName(a) || '';
        const bn = this.displayName(b) || '';
        return an.localeCompare(bn);
      });
    },
    /**
     * Returns the name of the currently-referenced section IF it's
     * archived AND not visible in the current list view. Drives the
     * banner that nudges the user to either pick a replacement or
     * toggle "Show archived" to restore it.
     */
    archivedCurrentBanner() {
      if (this.currentSectionId == null) return null;
      const visible = this.visibleSections.find(s => s.id === this.currentSectionId);
      if (visible) return null;  // Either in active list, or already shown via toggle
      const archived = this.sections.find(
        s => s.id === this.currentSectionId && s.status === 'archived',
      );
      if (!archived) return null;
      // Banner shows only when archived row exists but isn't currently
      // visible (i.e. toggle is off). When toggle is on, the row IS
      // visible and gets the "Current — archived" tag instead.
      if (this.showArchived) return null;
      return this.displayName(archived);
    },
    slugReadOnly() {
      return this.mode === 'edit' && (
        this.editingRefs.question_count > 0
        || this.editingRefs.questionnaire_count > 0
      );
    },
    slugError() {
      const slug = (this.form.slug || '').trim();
      if (!slug) return 'Slug is required.';
      if (!SLUG_REGEX.test(slug)) {
        return 'Slug must be lowercase letters, digits, hyphens, or underscores, and start with a letter (2–64 chars).';
      }
      // Client-side uniqueness check: compare against loaded sections.
      // Edit mode: skip the row we're editing.
      const collision = this.sections.some(
        s => s.slug === slug && s.id !== this.editingId,
      );
      if (collision) return `Slug "${slug}" is already used by another section in this organization.`;
      return null;
    },
    nameError() {
      if (!hasAnyLocaleFilled(this.form.name)) {
        return 'At least one language must be filled.';
      }
      return null;
    },
    isFormValid() {
      return !this.slugError && !this.nameError;
    },
  },
  watch: {
    sections: {
      handler() {
        // If we entered edit mode and the section's counts have changed
        // on a refetch, keep editingRefs in sync.
        if (this.editingId != null) {
          const row = this.sections.find(s => s.id === this.editingId);
          if (row) {
            this.editingRefs = {
              question_count: row.question_count || 0,
              questionnaire_count: row.questionnaire_count || 0,
            };
          }
        }
      },
      deep: false,
    },
    showArchived() {
      // Refetch with the new filter. The backend returns archived rows
      // alongside active when include_archived=true; toggling off
      // refetches without archived to keep the payload tight.
      this.fetchSections();
    },
  },
  mounted() {
    this.fetchSections();

    this._onResize = () => { this.isNarrowViewport = window.innerWidth < 800; };
    this._onResize();
    window.addEventListener('resize', this._onResize);

    // Esc on the document — works even when focus is in an input.
    this._onKeydown = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') this.onClose();
    };
    document.addEventListener('keydown', this._onKeydown);

    this.$nextTick(() => {
      if (this.$refs.closeBtn?.focus) this.$refs.closeBtn.focus();
    });
  },
  beforeUnmount() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
  },
  methods: {
    onClose() {
      this.$emit('close');
    },

    async fetchSections() {
      this.loading = true;
      this.loadError = null;
      try {
        const rows = await apiService.fetchSectionsWithCounts({
          includeArchived: this.showArchived,
        });
        this.sections = rows;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.loadError = typeof detail === 'string'
          ? detail
          : 'Could not load sections.';
      } finally {
        this.loading = false;
      }
    },

    displayName(section) {
      if (!section || !section.name) return section?.slug || '';
      const lang = this.activeLang;
      const v = section.name[lang];
      if (typeof v === 'string' && v.trim()) return v.trim();
      // Fallback chain through other locales.
      for (const loc of SUPPORTED_LOCALES) {
        if (loc === lang) continue;
        const f = section.name[loc];
        if (typeof f === 'string' && f.trim()) return f.trim();
      }
      return section.slug || '';
    },

    hasReferences(s) {
      return (s.question_count > 0) || (s.questionnaire_count > 0);
    },

    isLocaleFilled(loc) {
      const v = this.form.name && this.form.name[loc];
      return typeof v === 'string' && v.trim().length > 0;
    },

    pickSection(s) {
      this.$emit('select-section', s);
      this.onClose();
    },

    /* ───── Create flow ───── */

    openCreate() {
      this.mode = 'create';
      this.form = { slug: '', name: blankI18nName() };
      this.activeLang = DEFAULT_LOCALE;
      this.saveError = null;
      this.editingId = null;
      this.editingRefs = { question_count: 0, questionnaire_count: 0 };
    },

    onSlugInput() {
      // Lowercase as the user types — matches the slug constraint and
      // means a typed "Vegetables" doesn't fail validation just for case.
      this.form.slug = (this.form.slug || '').toLowerCase();
    },

    async submitCreate() {
      if (!this.isFormValid || this.saving) return;
      this.saving = true;
      this.saveError = null;
      try {
        const created = await apiService.createSectionForQuestionnaire(
          this.questionnaireSlug,
          {
            slug: this.form.slug.trim(),
            name: trimI18n(this.form.name),
          },
        );
        // Auto-emit-and-close: the popup was opened to pick a section;
        // creating one and immediately using it is the typical path.
        this.$emit('select-section', { ...created, question_count: 0, questionnaire_count: 0 });
        this.onClose();
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          this.saveError = typeof detail === 'string'
            ? detail
            : 'A section with that slug already exists.';
        } else if (typeof detail === 'string') {
          this.saveError = detail;
        } else {
          this.saveError = 'Could not create section. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    /* ───── Edit flow ───── */

    openEdit(s) {
      this.mode = 'edit';
      this.editingId = s.id;
      this.editingRefs = {
        question_count: s.question_count || 0,
        questionnaire_count: s.questionnaire_count || 0,
      };
      this.form = {
        slug: s.slug,
        name: { ...blankI18nName(), ...(s.name || {}) },
      };
      this.activeLang = DEFAULT_LOCALE;
      this.saveError = null;
    },

    cancelEdit() {
      this.mode = 'list';
      this.editingId = null;
      this.saveError = null;
    },

    async submitEdit() {
      if (!this.isFormValid || this.saving) return;
      this.saving = true;
      this.saveError = null;
      const payload = { name: trimI18n(this.form.name) };
      if (!this.slugReadOnly) {
        payload.slug = this.form.slug.trim();
      }
      try {
        const updated = await apiService.patchGlobalSection(this.editingId, payload);
        // Update local row in place.
        const idx = this.sections.findIndex(s => s.id === this.editingId);
        if (idx !== -1) {
          // Preserve counts (the patch endpoint doesn't return them).
          this.sections.splice(idx, 1, {
            ...updated,
            question_count: this.editingRefs.question_count,
            questionnaire_count: this.editingRefs.questionnaire_count,
          });
        }
        this.mode = 'list';
        this.editingId = null;
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          this.saveError = typeof detail === 'string'
            ? detail
            : 'A section with that slug already exists.';
        } else if (typeof detail === 'string') {
          this.saveError = detail;
        } else {
          this.saveError = 'Could not save changes. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    /* ───── Archive / restore / delete ───── */

    async confirmArchive() {
      if (!this.archiveConfirm || this.busy) return;
      this.busy = true;
      try {
        const updated = await apiService.archiveGlobalSection(this.archiveConfirm.id);
        const idx = this.sections.findIndex(s => s.id === this.archiveConfirm.id);
        if (idx !== -1) {
          this.sections.splice(idx, 1, {
            ...this.sections[idx],
            ...updated,
          });
        }
        this.archiveConfirm = null;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.loadError = typeof detail === 'string'
          ? detail
          : 'Could not archive the section.';
        this.archiveConfirm = null;
      } finally {
        this.busy = false;
      }
    },

    async restoreSection(s) {
      if (this.busy) return;
      this.busy = true;
      try {
        const updated = await apiService.restoreGlobalSection(s.id);
        const idx = this.sections.findIndex(row => row.id === s.id);
        if (idx !== -1) {
          this.sections.splice(idx, 1, {
            ...this.sections[idx],
            ...updated,
          });
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.loadError = typeof detail === 'string'
          ? detail
          : 'Could not restore the section.';
      } finally {
        this.busy = false;
      }
    },

    async confirmDelete() {
      if (!this.deleteConfirm || this.busy) return;
      if (this.deleteConfirm.typed !== this.deleteConfirm.section.slug) return;
      this.busy = true;
      try {
        await apiService.deleteGlobalSection(this.deleteConfirm.section.id);
        const idx = this.sections.findIndex(
          s => s.id === this.deleteConfirm.section.id,
        );
        if (idx !== -1) this.sections.splice(idx, 1);
        this.deleteConfirm = null;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.loadError = typeof detail === 'string'
          ? detail
          : 'Could not delete the section.';
        this.deleteConfirm = null;
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.section-picker-overlay {
  --color-bg-card: #ffffff;
  --color-bg-soft: #fafaf7;
  --color-bg-muted: #f5f4ef;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --color-warn-soft: rgba(232, 163, 61, 0.18);
  --color-warn-dark: #6B4A0F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4vh 2vw;
  z-index: 2500;
  font-family: var(--font-sans);
  box-sizing: border-box;
}

.section-picker-overlay *,
.section-picker-overlay *::before,
.section-picker-overlay *::after {
  box-sizing: border-box;
}

.section-picker {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  width: 480px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.section-picker.is-narrow {
  width: 100vw;
  max-width: none;
  height: 100vh;
  max-height: 100vh;
  border-radius: 0;
}

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 0.5px solid var(--color-border);
}

.popup-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
}

.back-link {
  background: none;
  border: none;
  font-family: inherit;
  font-size: 13px;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
}

.back-link:hover {
  text-decoration: underline;
}

.close-btn {
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: var(--radius-md);
  padding: 2px 10px;
  font-size: 22px;
  line-height: 1;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-family: inherit;
}

.close-btn:hover {
  background: var(--color-bg-soft);
  color: var(--color-text-primary);
  border-color: var(--color-border);
}

.popup-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 18px 18px;
}

.popup-body-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.error-banner {
  font-size: 13px;
  color: #6B1F18;
  background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  margin: 0 0 12px;
  line-height: 1.4;
}

.archived-current-banner {
  font-size: 12.5px;
  color: var(--color-warn-dark);
  background: var(--color-warn-soft);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  margin: 0 0 12px;
  line-height: 1.45;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.show-archived {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--color-text-secondary);
  user-select: none;
  cursor: pointer;
}

.show-archived input {
  accent-color: var(--color-accent);
  cursor: pointer;
}

.loading-state {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--color-text-tertiary);
  font-size: 13px;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-md);
}

.empty-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 12px;
  line-height: 1.5;
}

.section-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.section-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  transition: background 0.15s, border-color 0.15s;
}

.section-row:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.section-row.is-current {
  background: var(--color-accent-soft);
  border-color: var(--color-accent);
}

.section-row.is-archived {
  opacity: 0.75;
}

.row-main {
  flex: 1 1 auto;
  min-width: 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  padding: 0;
}

.row-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.current-archived-tag {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-warn-dark);
  background: var(--color-warn-soft);
  padding: 1px 6px;
  border-radius: 4px;
}

.row-meta {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  margin-top: 2px;
}

.row-actions {
  display: inline-flex;
  gap: 2px;
  flex-shrink: 0;
}

.icon-btn {
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: 4px;
  padding: 3px 7px;
  font-size: 14px;
  line-height: 1;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.icon-btn:hover:not(:disabled) {
  background: var(--color-bg-muted);
  color: var(--color-accent);
  border-color: var(--color-border);
}

.icon-btn.icon-archive:hover {
  color: var(--color-warn-dark);
}

.icon-btn.icon-restore:hover {
  color: var(--color-accent);
}

.icon-btn.icon-delete:hover:not(:disabled) {
  color: var(--color-bad);
}

.icon-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* ───── Form fields ───── */

.form-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  letter-spacing: 0.02em;
}

.field-input {
  padding: 8px 10px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 14px;
  color: var(--color-text-primary);
}

.field-input.mono {
  font-family: var(--font-mono);
  font-size: 13px;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.field-input:disabled {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
  cursor: not-allowed;
}

.field-input.has-error {
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.field-hint {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  margin: 0;
  line-height: 1.45;
}

.field-hint.dim {
  font-style: italic;
  color: var(--color-warn-dark);
  background: var(--color-warn-soft);
  border-radius: var(--radius-md);
  padding: 6px 10px;
}

.field-error {
  font-size: 12px;
  color: var(--color-bad);
  margin: 0;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}

.btn {
  padding: 8px 14px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.new-btn {
  /* slight emphasis vs the toggle on the right */
}

.mono-inline {
  font-family: var(--font-mono);
  font-size: 12px;
  background: var(--color-bg-muted);
  padding: 2px 6px;
  border-radius: 4px;
}

/* ───── Confirm dialogs (stacked on top of the popup) ───── */

.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2600;
}

.confirm-modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 18px;
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.confirm-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-primary);
}

.confirm-text {
  font-size: 13.5px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
